import { useState, useEffect } from 'react';
import { DynamicStripeContext } from 'hooks/useDynamicStripe';

// Cache the stripe promise to avoid multiple loadings
let stripePromise = null;

const getStripe = async () => {
  if (!stripePromise) {
    // Dynamic import to prevent bundling Stripe in main JS bundle
    const { loadStripe } = await import('@stripe/stripe-js');
    stripePromise = loadStripe(
      process.env.REACT_APP_STRIPE_KEY ||
        'pk_test_s9JBJsgJuCpTyuvM457NhmTU00Axogybz7'
    );
  }
  return stripePromise;
};

const LazyStripeProvider = ({ children }) => {
  const [stripe, setStripe] = useState(null);
  const [Elements, setElements] = useState(null);
  const [stripeHooks, setStripeHooks] = useState({
    useStripe: null,
    useElements: null,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Asynchronously load Stripe and Elements
    const loadStripeAsync = async () => {
      try {
        // Dynamic imports to prevent bundling
        const [
          { Elements: ElementsComponent, useStripe, useElements },
          stripeInstance,
        ] = await Promise.all([import('@stripe/react-stripe-js'), getStripe()]);

        setElements(() => ElementsComponent);
        setStripeHooks({ useStripe, useElements });
        setStripe(await stripeInstance);
      } catch (error) {
        console.error('Failed to load Stripe:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadStripeAsync();
  }, []);

  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        Loading payment system...
      </div>
    );
  }

  if (!stripe || !Elements) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#f44336' }}>
        Failed to load payment system. Please refresh the page.
      </div>
    );
  }

  const contextValue = {
    useStripe: stripeHooks.useStripe,
    useElements: stripeHooks.useElements,
    isLoaded: true,
  };

  return (
    <DynamicStripeContext.Provider value={contextValue}>
      <Elements stripe={stripe}>{children}</Elements>
    </DynamicStripeContext.Provider>
  );
};

export default LazyStripeProvider;
